import { useQuery } from "@tanstack/react-query";
import { practicePaymentApi } from "apis";
import type {
  CurrentSubscriptionResponseDto,
  TransactionPaginationDto,
  TransactionDto,
} from "libs/cellera-sdk";
import { useState, useCallback } from "react";
// Enums for better type safety and maintainability

export enum PlanDisplayName {
  MONTHLY = "Monthly Plan",
  SIX_MONTHS = "Six Months Plan",
  UNKNOWN = "Unknown Plan",
}

export enum SubscriptionStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  CANCELLED = "cancelled",
  PAST_DUE = "past_due",
}

export enum TransactionStatus {
  PAID = "paid",
  FAILED = "failed",
  PENDING = "pending",
}

export enum Currency {
  USD = "$",
}

// API Configuration Constants
export const API_CONFIG = {
  SUBSCRIPTION_STALE_TIME: 5 * 60 * 1000, // 5 minutes
  TRANSACTIONS_STALE_TIME: 2 * 60 * 1000, // 2 minutes
  DEFAULT_PAGE_SIZE: 10,
  INITIAL_PAGE: 1,
} as const;

export interface SubscriptionData {
  planName: string;
  price: string;
  renewalDate: string;
  status: SubscriptionStatus;
}

export interface Transaction {
  id: string;
  datetime: string;
  item: string;
  amount: number;
  status: TransactionStatus;
  hostedInvoiceUrl?: string;
  pdfUrl?: string;
  currency?: string;
}

interface UseSubscriptionReturn {
  subscription: SubscriptionData | null;
  transactions: Transaction[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  refetch: () => void;
  viewInvoice: (hostedInvoiceUrl: string) => void;
}

// Helper function to convert API response to frontend format
const convertSubscriptionData = (
  apiSubscription: CurrentSubscriptionResponseDto
): SubscriptionData => {
  // Map plan enum to display name using enum mapping
  const PLAN_NAMES = {
    1: "Monthly Plan",
    6: "Six Months Plan",
  };
  const INTERVAL_NAMES = {
    month: "month",
    months: "months",
  };
  const planName =
    PLAN_NAMES[
      apiSubscription.pricing?.intervalCount as keyof typeof PLAN_NAMES
    ] ?? "Unknown Plan";

  // Format price with currency and interval
  const { pricing } = apiSubscription || {};
  const { amount = 0, intervalCount = 1 } = pricing || {};
  const intervalName =
    intervalCount === 1
      ? INTERVAL_NAMES.month
      : intervalCount === 6
        ? INTERVAL_NAMES.months
        : INTERVAL_NAMES.month;

  const price = pricing
    ? `${Currency.USD} ${(amount / 100).toLocaleString("en-US", {
        minimumFractionDigits: 2,
      })}/${intervalCount === 1 ? "" : intervalCount} ${intervalName}`
    : "N/A";

  return {
    planName,
    price: price.replace(" ", ""),
    renewalDate: apiSubscription.renewalDateTime?.toISOString() || "",
    status: apiSubscription.status as SubscriptionStatus,
  };
};

// Fetch subscription data from API
const fetchSubscriptionData =
  async (): Promise<CurrentSubscriptionResponseDto> => {
    try {
      return await practicePaymentApi.getCurrentSubscription();
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      throw error;
    }
  };

// Fetch transactions data from API
const fetchTransactionsData = async (
  page: number = API_CONFIG.INITIAL_PAGE, // API uses 1-based pagination
  pageSize: number = API_CONFIG.DEFAULT_PAGE_SIZE
): Promise<TransactionPaginationDto> => {
  try {
    return await practicePaymentApi.getPracticeInvoices({
      page,
      pageSize,
    });
  } catch (error) {
    console.error("Error fetching transactions data:", error);
    throw error;
  }
};

export const useSubscription = (): UseSubscriptionReturn => {
  const [page, setPage] = useState<number>(API_CONFIG.INITIAL_PAGE); // Start with page 1 (1-based)
  const [pageSize, setPageSize] = useState<number>(
    API_CONFIG.DEFAULT_PAGE_SIZE
  );

  // Fetch subscription data
  const {
    data: subscriptionData,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    refetch: refetchSubscription,
  } = useQuery({
    queryKey: ["subscription"],
    queryFn: fetchSubscriptionData,
    staleTime: API_CONFIG.SUBSCRIPTION_STALE_TIME,
    refetchOnWindowFocus: false,
  });

  // Fetch transactions data
  const {
    data: transactionsData,
    isLoading: transactionsLoading,
    error: transactionsError,
    refetch: refetchTransactions,
  } = useQuery({
    queryKey: ["transactions", page, pageSize],
    queryFn: () => fetchTransactionsData(page, pageSize),
    staleTime: API_CONFIG.TRANSACTIONS_STALE_TIME,
    refetchOnWindowFocus: false,
  });

  const viewInvoice = useCallback((hostedInvoiceUrl: string) => {
    window.open(hostedInvoiceUrl, "_blank", "noopener,noreferrer");
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage + 1); // Convert from 0-based to 1-based
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(API_CONFIG.INITIAL_PAGE); // Reset to first page when changing page size
  }, []);

  const refetch = useCallback(() => {
    refetchSubscription();
    refetchTransactions();
  }, [refetchSubscription, refetchTransactions]);

  const loading = subscriptionLoading || transactionsLoading;
  const error =
    subscriptionError?.message || transactionsError?.message || null;

  return {
    subscription: subscriptionData
      ? convertSubscriptionData(subscriptionData)
      : null,
    transactions:
      transactionsData?.data?.map((transaction: TransactionDto) => ({
        id: transaction.id,
        datetime: transaction.datetime.toISOString(),
        item: transaction.item,
        amount: transaction.totalAmount,
        status: transaction.status as TransactionStatus,
        currency: transaction.currency,
        hostedInvoiceUrl: transaction.hostedInvoiceUrl,
        pdfUrl: transaction.pdfUrl,
      })) || [],
    totalCount: transactionsData?.total || 0,
    loading,
    error,
    page: page - 1, // Convert back to 0-based for UI
    pageSize,
    setPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    refetch,
    viewInvoice,
  };
};
